'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { toast } from '@/components/ui/Toasts/use-toast';
import { parseToastParams, stripToastParams } from '@/components/ui/Toasts/url-params';

// Turns `?status=…` / `?error=…` into a toast and scrubs those keys from the
// URL. This is the only component that knows toasts can arrive via the URL;
// client code should call toast() directly instead of routing.
//
// The scrub uses the native history API, not router.replace: Next ≥ 14.1
// syncs window.history.replaceState into useSearchParams/usePathname without
// fetching a new RSC payload, so no re-render of the page tree and no flash.
//
// Must be rendered inside <Suspense>: useSearchParams opts the nearest
// boundary into client rendering.
export function UrlToastBridge() {
  const searchParams = useSearchParams();
  // Keyed on the full query string so React StrictMode's double-invoked
  // effect in dev toasts once. Cleared whenever the URL is toast-free (which
  // it is right after our own scrub) so the same error arriving again later
  // (e.g. two wrong OTP codes in a row) toasts again.
  const handled = useRef<string | null>(null);

  useEffect(() => {
    const key = searchParams.toString();
    if (handled.current === key) return;

    const parsed = parseToastParams(searchParams);
    if (!parsed) {
      handled.current = null;
      return;
    }
    handled.current = key;

    toast({
      title: parsed.title,
      description: parsed.description ?? undefined,
      variant: parsed.variant,
    });

    const rest = stripToastParams(searchParams);
    if (rest === null) return;
    const url = `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`;
    // State must be null (not window.history.state): Next's patched
    // replaceState treats any state carrying its `__NA` marker as one of its
    // own internal calls and skips the router sync. With null it copies the
    // marker + tree over itself and dispatches, so useSearchParams updates.
    // Without that, a later router.push to the *same* toast URL (e.g. the
    // same wrong OTP twice) would be a no-op for every searchParams consumer.
    window.history.replaceState(null, '', url);
  }, [searchParams]);

  return null;
}
